import { computed, type Ref } from 'vue'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useHttp } from '@/shared/lib'
import type { ObjectPassportFieldDefinition } from './types'
import {
  buildObjectPassportLinkedListUrl,
  buildPassportBulkObjectsUrl,
} from './objectPassportLinkedTableApi'
import {
  parseLinkedTableResponse,
  parsePassportBulkNameMap,
} from './parseLinkedTableResponse'
import type { ObjectPassportLinkedListResult } from './linkedTableTypes'

export const OBJECT_PASSPORT_LINKED_TABLE_QUERY_KEY = 'object_passport_linked_table'

type PaginationFilter = {
  page: number
  perPage: number
}

/**
 * Загружает постраничный список связанных объектов для поля с `view_type: table`.
 * Имена и координаты обогащаются bulk-запросом паспортов дочерних id текущей страницы.
 */
export function useObjectPassportLinkedTable(options: {
  objectListId: Ref<number>
  field: Ref<ObjectPassportFieldDefinition | null>
  pagination: Ref<PaginationFilter>
  enabled?: Ref<boolean>
  statusDate?: Ref<string | null>
}) {
  const http = useHttp()
  const fieldId = computed(() => options.field.value?.id ?? 0)
  const statusDate = computed(() => options.statusDate?.value ?? null)

  const queryEnabled = computed(() => {
    if (options.enabled && !options.enabled.value) return false
    const oid = Number(options.objectListId.value)
    const fid = fieldId.value
    return Number.isFinite(oid) && oid > 0 && Number.isFinite(fid) && fid > 0
  })

  const query = useQuery({
    queryKey: computed(() => [
      OBJECT_PASSPORT_LINKED_TABLE_QUERY_KEY,
      options.objectListId.value,
      fieldId.value,
      options.pagination.value.page,
      options.pagination.value.perPage,
      statusDate.value,
    ]),
    enabled: queryEnabled,
    retry: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ObjectPassportLinkedListResult> => {
      const oid = Number(options.objectListId.value)
      const fid = fieldId.value
      const { page, perPage } = options.pagination.value

      const linkedUrl = buildObjectPassportLinkedListUrl(oid, fid, page, perPage, statusDate.value)
      const linkedRes = await http.get(linkedUrl)
      const linkedRaw = { data: linkedRes.data, payloads: linkedRes.payload }
      const linkedParsed = parseLinkedTableResponse(linkedRaw, page, perPage)

      const objectIds = linkedParsed.rows.map((row) => row.objectId)
      const bulkUrl = buildPassportBulkObjectsUrl(objectIds)
      if (!bulkUrl) return linkedParsed

      const bulkRes = await http.get(bulkUrl)
      const enrichMap = parsePassportBulkNameMap({ data: bulkRes.data })
      return parseLinkedTableResponse(linkedRaw, page, perPage, enrichMap)
    },
  })

  return {
    rows: computed(() => query.data.value?.rows ?? []),
    meta: computed(() => query.data.value?.meta ?? { page: 1, perPage: 10, total: 0 }),
    isLoading: computed(() => query.isLoading.value || query.isFetching.value),
    isError: computed(() => query.isError.value),
    refetch: query.refetch,
  }
}

/**
 * Инвалидирует кэш linked-таблиц и нормализованного паспорта после unlink.
 */
export async function invalidateObjectPassportLinkedTableQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  objectListId: number,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [OBJECT_PASSPORT_LINKED_TABLE_QUERY_KEY, objectListId],
    }),
    queryClient.invalidateQueries({
      queryKey: ['load_object_passport_fields', `/objects/${objectListId}/passport`],
    }),
    queryClient.invalidateQueries({ queryKey: ['load_invent_list_item'] }),
  ])
}
